import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import SectionHeader from './SectionHeader';
import PopularDishCard from './PopularDishCard';

const SCROLL_STEP = 200;

const DishCardSkeleton = () => (
  <div className="snap-start shrink-0 w-36 sm:w-40 animate-pulse">
    <div className="w-full aspect-square rounded-2xl bg-gray-200" />
    <div className="mt-4 space-y-2 px-0.5">
      <div className="h-3.5 bg-gray-200 rounded w-4/5" />
      <div className="h-3.5 bg-gray-200 rounded w-1/2" />
    </div>
  </div>
);

const PopularDishesCarousel = ({ dishes = [], loading, onDishCardClick, onDishPlusClick }) => {
  const { t } = useTranslation();
  const trackRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    updateScrollState();
  }, [dishes, loading, updateScrollState]);

  const scrollBy = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * SCROLL_STEP, behavior: 'smooth' });
  };

  const scrollToEnd = () => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollTo({ left: el.scrollWidth, behavior: 'smooth' });
  };

  if (!loading && dishes.length === 0) return null;

  return (
    <section className="mb-10">
      <SectionHeader
        title={t('home.popularDishes')}
        onSeeAllClick={scrollToEnd}
        onScrollLeft={() => scrollBy(-1)}
        onScrollRight={() => scrollBy(1)}
        canScrollLeft={canScrollLeft}
        canScrollRight={canScrollRight}
      />

      <div className="relative -mx-4 sm:mx-0">
        <div
          className="absolute left-0 top-0 bottom-0 w-10 sm:w-16 z-10 bg-gradient-to-r from-white to-transparent pointer-events-none"
          style={{ opacity: canScrollLeft ? 1 : 0, transition: 'opacity 0.2s' }}
        />
        <div
          className="absolute right-0 top-0 bottom-0 w-10 sm:w-16 z-10 bg-gradient-to-l from-white to-transparent pointer-events-none"
          style={{ opacity: canScrollRight ? 1 : 0, transition: 'opacity 0.2s' }}
        />

        <div
          ref={trackRef}
          onScroll={updateScrollState}
          className="flex gap-4 overflow-x-auto snap-x snap-mandatory scroll-smooth px-4 sm:px-0 pb-2"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          <style>{`section .flex.overflow-x-auto::-webkit-scrollbar { display: none; }`}</style>
          {loading
            ? [...Array(6)].map((_, i) => <DishCardSkeleton key={i} />)
            : dishes.map((dish) => (
                <PopularDishCard
                  key={dish.id}
                  dish={dish}
                  onCardClick={onDishCardClick}
                  onPlusClick={onDishPlusClick}
                />
              ))
          }
        </div>
      </div>
    </section>
  );
};

export default PopularDishesCarousel;
